
import Vue from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import { MyHotelLocalNavi } from '../-components';
import HotelViewRoot from '../../-index';
import { ActivityCommons, Activity } from '~/schemes';
import html2text from '~/helpers/html2text';
import {
  HIconLink,
  HImg,
  HBtn,
  HPortalItem,
  HSimplePaginationItem,
  HSimpleDotPagination,
} from '~/components';
import {
  createPeriodText,
  createAgeRangeText,
  translatedTimeRequiredText,
} from '~/helpers';

/**
 * CMS化した際に、既存のものからID（URL）が変わったものがあるので
 * リダイレクトフォローする
 */
const REDIRECT_ID_MAP = {
  '1': '4032',
  '2': '4059',
  '3': '4044',
  '4': '1401',
  '5': '',
  '6': '4035 ',
  '7': '4038',
  '8': '4041',
  '9': '4018',
  '10': '',
  '11': '4071',
  '12': '',
  '13': '',
  '14': '2672',
  '15': '3611',
  '16': '4012',
  '17': '',
  '18': '4015',
  '19': '',
  '20': '4025',
  '21': '4074',
  '22': '4078',
  '23': '4021',
  '24': '',
  '25': '',
  '26': '',
  '27': '',
  '28': '4062',
  '29': '4065',
  '30': '4068',
  '31': '1398',
  '32': '',
  '33': '',
  '34': '',
  '35': '',
  '36': '4047',
  '37': '4050',
  '38': '4053',
  '39': '',
  '40': '4056',
  '41': '3473',
  '42': '4029',
};

@Component<MyView>({
  inject: ['hotelViewRoot'],
  middleware({ params, redirect }) {
    if (process.server) {
      const { lang, hotel_slug, activity_id } = params;
      if (hotel_slug !== 'aomoriya') {
        return;
      }
      const redirectId = REDIRECT_ID_MAP[activity_id];
      if (typeof redirectId === 'string') {
        const redirectTo =
          `/${lang}/hotels/${hotel_slug}/activities/` +
          (redirectId ? `${redirectId}/` : '');

        return redirect(301, redirectTo);
      }
    }
  },
  async asyncData({ $commons, $api, $hotel, params, error, redirect, $error }) {
    try {
      const { hotel_slug, activity_id } = params;

      const hotelSource = await $hotel.getBySlug(hotel_slug);
      if (!hotelSource) {
        return error({
          statusCode: 404,
        });
      }

      /**
       * アクティビティー基本設定がなければ施設TOPにリダイレクトしてあげる
       */
      if (!hotelSource.activitySettings) {
        const location = $hotel.location(undefined, hotelSource);
        return redirect(location);
      }

      const [activityCommons, activity] = await Promise.all([
        $commons.getActivity(),
        $hotel.activityBySlugAndId(hotel_slug, activity_id).catch((err) => {
          if (
            $api.isAxiosError(err) &&
            err.response &&
            err.response.status === 404
          ) {
            return;
          }
          throw err;
        }),
      ]);
      if (!activity) {
        error({ statusCode: 404 });
        return;
      }

      /**
       * 特定言語のみ準備中の場合は固定文字 `@draft` がレスポンスされるので、
       * 一覧ページへリダイレクトしてあげる
       */
      if (activity.name === '@draft') {
        const location = $hotel.location('/activities/', hotelSource);
        return redirect(location);
      }

      return {
        activityCommons,
        activity,
      };
    } catch (_err) {
      $error.throw(_err);
    }
  },
  head() {
    const { hotel, activity } = this;
    const title = activity.name;
    const description = html2text(activity.description).replace(/\n/g, '');
    const head: MetaInfo = {
      title,
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/activities/${this.slug}/`,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.$res.img(activity.mainImage),
        },
      ],
    };
    if (description) {
      head.meta!.push(
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }
    return head;
  },
  render() {
    const {
      activity,
      timeRequired,
      targetAgeText,
      hostLanguage,
      reservationUrl,
      periodText,
      periodMemo,
      paginationItems,
    } = this;
    const {
      mainImage,
      subImage,
      title,
      times,
      includedContent,
      priceInfo,
      capacity,
      place,
      belongings,
      schedule,
      applicationDeadline,
      precautions,
      footnote,
    } = activity;
    return (
      <div staticClass="my-hotel-activity-detail-view">
        <MyHotelLocalNavi />
        <div staticClass="my-inner">
          <div staticClass="my-contents h-container h-container--min">
            <p staticClass="my-back-to-list my-back-to-list--top">
              <HIconLink back to={this.listLocation}>
                {this.$t('action.backToList', {
                  listName: this.activitySectionLabel,
                })}
              </HIconLink>
            </p>

            <div staticClass="my-activity">
              <h1 staticClass="my-activity__name">{activity.name}</h1>
              <div staticClass="my-activity__main-img">
                <HImg
                  staticClass="my-activity__main-img__node"
                  corner
                  src={this.$res.img(mainImage)}
                />
              </div>
              <div staticClass="my-activity__columns">
                <div staticClass="my-activity__columns__column">
                  <h2 staticClass="my-activity__title">{title}</h2>
                  <div
                    v-wysiwyg={activity.description}
                    staticClass="my-activity__description"
                  />
                </div>
                {/* 魅力紹介 */}
                <div staticClass="my-activity__columns__column">
                  {this.activity.attractiveItems.map((item) => {
                    return (
                      <div staticClass="my-activity__attractive">
                        {item.images && (
                          <HSimpleDotPagination
                            staticClass="my-activity__attractive__images"
                            items={paginationItems(item.images)}
                            aspect-ratio="3/2"
                          />
                        )}
                        {item.title && (
                          <div staticClass="my-activity__attractive__title">
                            {item.title}
                          </div>
                        )}
                        {item.introductionText && (
                          <div
                            staticClass="my-activity__attractive__introduction-text"
                            v-wysiwyg={item.introductionText}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div staticClass="my-activity__columns__column">
                  {!!subImage && (
                    <HImg
                      staticClass="my-activity__sub-img"
                      corner
                      src={this.$res.img(subImage)}
                    />
                  )}
                </div>
                <div staticClass="my-activity__columns__column">
                  <table staticClass="my-activity__table">
                    <thead>
                      <tr>
                        <th staticClass="my-activity__table__title" colspan={2}>
                          {activity.name}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/** 期間 */}
                      <tr>
                        <th>{this.$t('label.period')}</th>
                        <td>
                          <span staticClass="my-activity__period-text">
                            {periodText}
                          </span>
                          {!!periodMemo && (
                            <span staticClass="my-activity__period-memo">
                              {periodMemo}
                            </span>
                          )}
                        </td>
                      </tr>
                      {/** 開催時間 */}
                      {!!times && (
                        <tr>
                          <th>{this.$t('label.timeToHold')}</th>
                          <td v-wysiwyg={times} />
                        </tr>
                      )}
                      {/** 所要時間 */}
                      {!!timeRequired && (
                        <tr>
                          <th>{this.$t('label.timeRequired')}</th>
                          <td v-wysiwyg={timeRequired || ''} />
                        </tr>
                      )}
                      {/** 対象年齢 */}
                      {!!targetAgeText && (
                        <tr>
                          <th>{this.$t('label.appropriateAge')}</th>
                          <td v-wysiwyg={targetAgeText} />
                        </tr>
                      )}
                      {/** 含まれる内容 */}
                      {!!includedContent && (
                        <tr>
                          <th>{this.$t('label.includedContent')}</th>
                          <td v-wysiwyg={includedContent} />
                        </tr>
                      )}
                      {/** 料金 */}
                      {!!priceInfo && (
                        <tr>
                          <th>{this.$t('label.price')}</th>
                          <td v-wysiwyg={priceInfo} />
                        </tr>
                      )}
                      {/** 定員 */}
                      {!!capacity && (
                        <tr>
                          <th>{this.$t('label.peopleQuota')}</th>
                          <td v-wysiwyg={capacity} />
                        </tr>
                      )}
                      {/** 集合場所 */}
                      {!!place && (
                        <tr>
                          <th>{this.$t('label.meetingSpot')}</th>
                          <td v-wysiwyg={place} />
                        </tr>
                      )}
                      {/** 持ち物 */}
                      {!!belongings && (
                        <tr>
                          <th>{this.$t('label.belongings')}</th>
                          <td v-wysiwyg={belongings} />
                        </tr>
                      )}
                      {/** スケジュール */}
                      {!!schedule && (
                        <tr>
                          <th>{this.$t('label.schedule')}</th>
                          <td v-wysiwyg={schedule} />
                        </tr>
                      )}
                      {/** 開催言語 */}
                      {!!hostLanguage && (
                        <tr>
                          <th>{this.$t('label.hostLanguage')}</th>
                          <td v-wysiwyg={hostLanguage} />
                        </tr>
                      )}
                      {/** 申し込み期限 */}
                      {!!applicationDeadline && (
                        <tr>
                          <th>{this.$t('label.applicationDeadline')}</th>
                          <td v-wysiwyg={applicationDeadline} />
                        </tr>
                      )}
                      {/** 注意事項 */}
                      {!!precautions && (
                        <tr>
                          <th>{this.$t('label.notes')}</th>
                          <td v-wysiwyg={precautions} />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* 後注 */}
                {footnote && (
                  <div
                    staticClass="my-activity__columns__column"
                    class="my-activity__footnote"
                    v-wysiwyg={footnote}
                  />
                )}
              </div>

              {!!reservationUrl && (
                <HBtn
                  staticClass="my-activity__to-urakata"
                  href={reservationUrl}
                  stack={this.reservationNeedLogin}
                  stack-font-default
                  target="_blank"
                  block
                  color="plain">
                  {this.$t('action.reserveActivity')}
                </HBtn>
              )}
            </div>
            <p staticClass="my-back-to-list my-back-to-list--bottom">
              <HIconLink back to={this.listLocation}>
                {this.$t('action.backToList', {
                  listName: this.activitySectionLabel,
                })}
              </HIconLink>
            </p>
          </div>

          {!!reservationUrl && (
            <HPortalItem to="portal-footer">
              <div staticClass="my-hotel-activity-detail-view__to-urakata">
                <HBtn
                  staticClass="my-hotel-activity-detail-view__to-urakata__btn"
                  href={reservationUrl}
                  stack={this.reservationNeedLogin}
                  stackFontDefault
                  target="_blank"
                  block
                  color="plain">
                  {this.$t('action.reserveActivity')}
                </HBtn>
              </div>
            </HPortalItem>
          )}
        </div>
      </div>
    );
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;

  activityCommons: ActivityCommons = null as any;
  activity: Activity = null as any;

  get slug() {
    return this.$route.params.activity_id;
  }

  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  get listLocation() {
    return this.$hotel.location('/activities', this.hotel);
  }

  /**
   * テーマで設定されているアクティビティナビゲーション名
   */
  get activitySectionLabel() {
    return this.$theme.getSectionLongLabelWithoutHTML('activities');
  }

  get reservationUrl() {
    return this.activity.reservationUrl;
  }

  get reservationNeedLogin() {
    return this.$hotel.detectReservationNeedLoginGuide(this.reservationUrl);
  }

  get activitySettings() {
    const { activitySettings } = this.hotel;
    if (!activitySettings) {
      throw new Error('missing activitySettings');
    }
    return activitySettings;
  }

  get hostLanguage() {
    const { hostLanguage } = this.activity;
    if (hostLanguage) {
      const labels: string[] = [];
      hostLanguage.forEach((lang) => {
        const label = this.$t(`language.${lang}`) as string;
        label && labels.push(label);
      });
      if (labels.length) {
        return labels.join(this.$t('chore.wordSparator') as string);
      }
    }
  }

  // get headStyles() {
  //   return {
  //     backgroundImage: `url(${this.$res.img(this.activitySettings.image)})`,
  //   };
  // }

  get periodText(): string {
    const { period, periodText } = this.activity;
    if (periodText) return periodText;
    const from = (period && period.from) || null;
    const to = (period && period.to) || null;
    return createPeriodText(this, from, to);
  }

  get timeRequired() {
    const { minutes, text } = this.activity.timeRequired;
    if (text) {
      return text;
    }
    if (minutes) {
      return translatedTimeRequiredText(this, minutes);
      // const hours = Math.floor(_minutes / 60);
      // const isAnHour = hours === 1;
      // let minutes = _minutes - hours * 60;
      // let i18nKey: string;

      // if (minutes === 0) {
      //   i18nKey = isAnHour ? 'anHour' : 'hours';
      // } else if (_minutes < 100) {
      //   i18nKey = 'minutes';
      //   minutes = _minutes;
      // } else {
      //   i18nKey = isAnHour ? 'anHourAndMinutes' : 'hoursAndMinutes';
      // }
      // return this.$t(`value.timeRequired.${i18nKey}`, {
      //   hours,
      //   minutes,
      // }) as string;
    }
  }

  get periodMemo() {
    return this.activity.periodMemo;
  }

  get targetAgeText(): string | void {
    const data = this.activity;
    const { targetAge, targetAgeRange } = data;
    if (targetAgeRange) {
      return createAgeRangeText(this, targetAgeRange.from, targetAgeRange.to);
    }
    const hit = this.$commons
      .getActivityTargetAges()
      .find((r) => r.value === targetAge);
    if (hit) {
      return hit.name;
    }
  }

  /**
   * 魅力紹介の画像表示
   * @memo 現在は1枚登録にしているが、長期的に複数登録したい需要も出るかもしれないのでページネーションとして設定している
   * */
  private paginationItems(images: string[]): HSimplePaginationItem[] {
    return images.map((image) => ({ image }));
  }
}
